import { XMLParser } from "fast-xml-parser";
import type { MetaArgs } from "react-router";

export type HreflangAlternate = {
  hreflang: string;
  href: string;
};

export type HreflangMap = Map<string, Array<HreflangAlternate>>;

type RootLoaderData = {
  hreflangAlternates: Array<HreflangAlternate>;
};

export type RouteMetaMatch = MetaArgs["matches"][number];

type SitemapLink = { "@_hreflang"?: string; "@_href"?: string };
type SitemapUrl = {
  loc?: string;
  "xhtml:link"?: Array<SitemapLink>;
};
type SitemapXml = { urlset?: { url?: Array<SitemapUrl> } };
type InflightMap = { uuid: string; promise: Promise<HreflangMap> };

const xmlParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: "@_",
  isArray: (name) => name === "url" || name === "xhtml:link",
});

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isHreflangAlternate(value: unknown): value is HreflangAlternate {
  return (
    isObject(value) &&
    typeof value.hreflang === "string" &&
    typeof value.href === "string"
  );
}

function isRootLoaderData(value: unknown): value is RootLoaderData {
  return (
    isObject(value) &&
    Array.isArray(value.hreflangAlternates) &&
    value.hreflangAlternates.every(isHreflangAlternate)
  );
}

function pathWithoutLocale(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  const isLang = /^[a-z]{2}(-[a-zA-Z]{2})?$/.test(first ?? "");
  const remainder = isLang ? segments.slice(1) : segments;

  return remainder.length > 0 ? `/${remainder.join("/")}` : "/";
}

// Strip leading "/{lang}" or "/{lang}-{country}" from a URL pathname.
// Must match neighbour-puller's `canonicalPathFromUrl` semantics in CMS extension.
export function canonicalPath(
  rawUrl: string,
  expectedHost: string,
): string | null {
  try {
    const url = new URL(rawUrl);
    return url.host === expectedHost ? pathWithoutLocale(url.pathname) : null;
  } catch {
    return null;
  }
}

export function normalisePath(pathname: string): string {
  return pathWithoutLocale(pathname);
}

function parseXml(xml: string, selfHost: string): HreflangMap {
  let sitemap: SitemapXml;
  try {
    sitemap = xmlParser.parse(xml) as SitemapXml;
  } catch {
    return new Map();
  }

  const byPath = new Map<string, Map<string, string>>();
  for (const url of sitemap.urlset?.url ?? []) {
    const path = url.loc ? canonicalPath(url.loc, selfHost) : null;
    if (path === null) continue;

    const links = byPath.get(path) ?? new Map<string, string>();
    byPath.set(path, links);

    for (const link of url["xhtml:link"] ?? []) {
      if (link["@_hreflang"] && link["@_href"]) {
        links.set(link["@_hreflang"], link["@_href"]);
      }
    }
  }

  const result: HreflangMap = new Map();
  for (const [path, links] of byPath) {
    result.set(
      path,
      Array.from(links.entries()).map(([hreflang, href]) => ({
        hreflang,
        href,
      })),
    );
  }
  return result;
}

type GetHreflangMapParams = {
  cmsPublicPath: string;
  sitemapUuid: string | null;
  selfHost: string;
};

class HreflangMapLoader {
  private uuid: string | null = null;
  private map: HreflangMap = new Map();
  private inflight: InflightMap | null = null;

  public get(params: GetHreflangMapParams): Promise<HreflangMap> {
    const { sitemapUuid } = params;
    if (!sitemapUuid) return Promise.resolve(new Map());
    if (this.uuid === sitemapUuid) return Promise.resolve(this.map);
    if (this.inflight?.uuid === sitemapUuid) return this.inflight.promise;

    const promise = this.load({ ...params, sitemapUuid }).finally(() => {
      if (this.inflight?.uuid === sitemapUuid) this.inflight = null;
    });
    this.inflight = { uuid: sitemapUuid, promise };

    return promise;
  }

  private async load(
    params: GetHreflangMapParams & { sitemapUuid: string },
  ): Promise<HreflangMap> {
    try {
      const url = `${params.cmsPublicPath.replace(/\/$/, "")}/assets/${params.sitemapUuid}`;
      const response = await fetch(url);
      if (!response.ok) return new Map();

      const xml = await response.text();
      const map = parseXml(xml, params.selfHost);
      this.uuid = params.sitemapUuid;
      this.map = map;
      return map;
    } catch {
      return new Map();
    }
  }
}

const hreflangMapLoader = new HreflangMapLoader();

export function getHreflangMap(
  params: GetHreflangMapParams,
): Promise<HreflangMap> {
  return hreflangMapLoader.get(params);
}

export function getRootHreflangAlternates(
  matches: Array<RouteMetaMatch>,
): Array<HreflangAlternate> {
  const root = matches.find((match) => match.id === "root");
  const data = root?.loaderData ?? root?.data;

  return isRootLoaderData(data) ? data.hreflangAlternates : [];
}

export function lookupAlternates(
  map: HreflangMap,
  pathname: string,
): Array<HreflangAlternate> {
  return map.get(normalisePath(pathname)) ?? [];
}
